import { useMomentRecipientsClassify } from "../../../../../hooks/use-moment-recipients-classify";
import { useDynamicSegmentRecipients } from "./use-dynamic-segment-recipients";
import { useParsedRecipients } from "./use-parsed-recipients";
import { usePersonInUnsavedSegmentWithConnections } from "./use-person-in-unsaved-segment-with-connections";
import { useSelectedPreviewInfo } from "./use-selected-preview-info";

import type { UseSupabaseLoaderState } from "../../../../../../../server/supabase/hooks/types";
import type { UUID } from "../../../../../../../utils/uuid";
import type { MomentRecipientsClassify } from "../../../../../hooks/use-moment-recipients-classify";
import type { PreviewMomentData } from "../types";
import type { DynamicSegmentRecipientData } from "./use-dynamic-segment-recipients";
import type { PreviewRecipientInfo, PreviewSubject } from "./use-selected-preview-info";

export interface MomentPreviewInfo {
  subjects: PreviewSubject[];
  selectedPreviewInfo: PreviewRecipientInfo;
  setSelectedPreviewInfo: (subject: UUID | null) => void;
  dynamicSegmentRecipients: DynamicSegmentRecipientData;
  classifyData: MomentRecipientsClassify;
}

interface Data {
  id: UUID;
  channel: PreviewMomentData["channel"];
  segment_json: NonNullable<PreviewMomentData["segment"]>["segment_json"] | null;
  target_connection_type: PreviewMomentData["target_connection_type"];
  dynamic_segment_id_map: PreviewMomentData["dynamic_segment_id_map"];
  additional_recipients: PreviewMomentData["additional_recipients"];
  cc_bcc: PreviewMomentData["cc_bcc"];
  segment_operation: NonNullable<PreviewMomentData["segment"]>["operation"] | null;
  base_segment_name: NonNullable<PreviewMomentData["segment"]>["name"] | null;
  target_channel: PreviewMomentData["target_channel"];
  target_manager: PreviewMomentData["target_manager"];
  only_additional_recipients: PreviewMomentData["only_additional_recipients"];
  notify_channel: PreviewMomentData["notify_channel"];
}

export function usePreviewInfo({
  id,
  channel,
  segment_json,
  target_connection_type,
  dynamic_segment_id_map,
  additional_recipients,
  cc_bcc,
  segment_operation,
  base_segment_name,
  target_channel,
  target_manager,
  only_additional_recipients,
  notify_channel,
}: Data): UseSupabaseLoaderState<MomentPreviewInfo> {
  const { loading, error, data } = usePersonInUnsavedSegmentWithConnections({
    channel,
    segment_json,
    target_connection_type,
  });

  const {
    loading: loadingDynamicSegmentRecipients,
    data: dynamicSegmentRecipients,
    error: dynamicSegmentRecipientsError,
  } = useDynamicSegmentRecipients({
    channel,
    dynamic_segment_id_map,
    target_connection_type,
  });

  const {
    loading: loadingClassifyData,
    error: errorClassifyData,
    data: classifyData,
  } = useMomentRecipientsClassify({
    channel,
    additional_recipients,
    cc_bcc,
    segment_operation,
    base_segment_name,
    target_channel,
    target_manager,
    only_additional_recipients,
    notify_channel,
    target_connection_type,
  });

  const parsedData = useParsedRecipients(data, classifyData);

  const [selectedPreviewInfo, setSelectedPreviewInfo] = useSelectedPreviewInfo({
    parsedData,
    classifyData,
    momentId: id,
  });

  if (loading || loadingClassifyData || loadingDynamicSegmentRecipients)
    return { data: null, loading: true, error: null };

  if (error) return { data: null, loading: false, error };

  if (errorClassifyData) return { data: null, loading: false, error: errorClassifyData };

  if (dynamicSegmentRecipientsError)
    return { data: null, loading: false, error: dynamicSegmentRecipientsError };

  return {
    loading: false,
    error: null,
    data: {
      subjects: parsedData,
      selectedPreviewInfo,
      setSelectedPreviewInfo,
      dynamicSegmentRecipients,
      classifyData: classifyData!,
    },
  };
}
